import { Accordion } from '@base-ui/react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import dayjs from 'dayjs';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '~/api/user';
import { Button, Card, Illustration, Separator, Text } from '~/components';
import { useIntl } from '~/hooks/useIntl';

import {
  commissionDetailsLineStyle,
  commissionDetailsPercentageStyle,
  commissionDetailsStyle,
  commissionExplanationStyle,
  commissionHeaderCommissionStyle,
  commissionHeaderTextStyle,
  commissionSubTitleStyle,
  commissionTitleStyle,
  containerStyle,
  contentStyle,
  headerStyle,
  headerWrapperStyle,
  illustrationStyle,
  illustrationWrapperStyle,
  labelsRowStyle,
  panelStyle,
  separatorStyle,
} from './PricingInfo.css';
import {
  showAdminOverrideTag,
  showEarlyAdopterTag,
  showFreeTrialTag,
  showLoyalUserTag,
  showReferralDiscountTag,
  showYearlyCopyrightLimitExceededTag,
} from './PricingInfo.utils';
import { PricingInfoLabel } from './PricingInfoLabel';

export const PricingInfo = () => {
  const { data: user } = useUser();

  const [showDetails, setShowDetails] = useState(false);

  const [ref] = useAutoAnimate();
  const { formatDate, formatPercentage } = useIntl();
  const { t } = useTranslation(['pricing']);

  const activeInvitorDiscounts = user.invitorDiscounts
    .filter(({ isActive }) => isActive)
    .sort(({ validUntil: a }, { validUntil: b }) => (dayjs(a).isBefore(dayjs(b)) ? 1 : -1));

  const totalDiscountPercentage = Math.min(
    activeInvitorDiscounts.reduce((totalPercentage, { percentage }) => totalPercentage + percentage, 0),
    1,
  );
  const hasDiscount = user.inviteeDiscount !== null || activeInvitorDiscounts.length > 0;

  const commissionPercentage = user.yearlyCopyrightLimitExceeded ? 0 : user.commissionSettings.creativePercentage / 100;
  const commissionPercentageWithPromo = user.inviteeDiscount?.type === 'ShelterForFree' ? 0 : commissionPercentage;
  const commissionPercentageWithDiscount = commissionPercentageWithPromo * (1 - totalDiscountPercentage);

  return (
    <Card.Root ref={ref}>
      <section className={containerStyle}>
        <Accordion.Root
          className={contentStyle}
          onValueChange={(value) => setShowDetails(value.includes('details'))}
          value={showDetails ? ['details'] : []}
        >
          <Accordion.Item className={headerWrapperStyle} value="details">
            <h2 className={commissionHeaderTextStyle}>
              <span className={commissionHeaderCommissionStyle}>{formatPercentage(commissionPercentageWithDiscount)}</span>

              {t('pricing:pricingInfo.commission.title')}
            </h2>

            <div className={labelsRowStyle}>
              {showReferralDiscountTag(user) && (
                <PricingInfoLabel
                  label={t('pricing:pricingInfo.commission.deviations.referralDiscount.label', {
                    amount: formatPercentage(-1 * totalDiscountPercentage),
                  })}
                  variant="success"
                />
              )}

              {showLoyalUserTag(user) && (
                <PricingInfoLabel
                  label={t('pricing:pricingInfo.commission.deviations.loyalUser.label')}
                  tooltip={t('pricing:pricingInfo.commission.deviations.loyalUser.tooltip')}
                  variant="success"
                />
              )}

              {showEarlyAdopterTag(user) && (
                <PricingInfoLabel label={t('pricing:pricingInfo.commission.deviations.earlyAdopter.label')} variant="success" />
              )}

              {showFreeTrialTag(user) && (
                <PricingInfoLabel
                  label={t('pricing:pricingInfo.commission.deviations.premiumForFree.label', {
                    count: user.inviteeDiscount.daysActive,
                  })}
                  tooltip={t('pricing:pricingInfo.commission.deviations.premiumForFree.tooltip', {
                    expirationDate: formatDate(
                      dayjs()
                        .add(user.inviteeDiscount.daysActive - 1, 'days')
                        .toDate(),
                    ),
                  })}
                  variant="success"
                />
              )}

              {showYearlyCopyrightLimitExceededTag(user) && (
                <PricingInfoLabel
                  label={t('pricing:pricingInfo.commission.deviations.yearlyCopyrightLimitExceeded.label')}
                  tooltip={t('pricing:pricingInfo.commission.deviations.yearlyCopyrightLimitExceeded.explanation')}
                  variant="warning"
                />
              )}

              {showAdminOverrideTag(user) && (
                <PricingInfoLabel
                  label={t('pricing:pricingInfo.commission.deviations.adminOverride.label')}
                  tooltip={t('pricing:pricingInfo.commission.deviations.adminOverride.explanation')}
                  variant="warning"
                />
              )}
            </div>

            {hasDiscount && (
              <Accordion.Header>
                <Accordion.Trigger
                  render={
                    <Button emphasis="tertiary" icon="ArrowDown" iconPlacement="right">
                      {t('pricing:pricingInfo.commission.details.button')}
                    </Button>
                  }
                />
              </Accordion.Header>
            )}

            <Accordion.Panel className={panelStyle}>
              <Separator className={separatorStyle} />

              <div className={commissionDetailsStyle}>
                <span>{t('pricing:pricingInfo.commission.details.commissionWithoutDiscount')}</span>
                <span className={commissionDetailsPercentageStyle}>{formatPercentage(commissionPercentage)}</span>

                {user.inviteeDiscount?.type === 'ShelterForFree' && (
                  <>
                    <span>{t('pricing:pricingInfo.commission.details.freeTrial', { count: user.inviteeDiscount.daysValid })}</span>
                    <span className={commissionDetailsPercentageStyle}>{formatPercentage(-1 * commissionPercentage)}</span>
                  </>
                )}

                {commissionPercentageWithPromo > 0 &&
                  activeInvitorDiscounts.map((discount) => (
                    <Fragment key={discount.id}>
                      <span>{t('pricing:pricingInfo.commission.details.referralDiscount', { inviteeName: discount.inviteeName })}</span>
                      <span className={commissionDetailsPercentageStyle}>
                        {formatPercentage(-1 * commissionPercentageWithPromo * discount.percentage)}
                      </span>
                    </Fragment>
                  ))}

                <Separator aria-hidden className={commissionDetailsLineStyle} />

                <span>{t('pricing:pricingInfo.commission.details.commissionWithDiscount')}</span>
                <span className={commissionDetailsPercentageStyle}>{formatPercentage(commissionPercentageWithDiscount)}</span>
              </div>
            </Accordion.Panel>
          </Accordion.Item>

          <Separator aria-hidden />

          <div className={headerStyle}>
            <Text className={commissionTitleStyle}>{t('pricing:pricingInfo.commission.explanation.title')}</Text>

            <Text className={commissionSubTitleStyle}>{t('pricing:pricingInfo.commission.explanation.subTitle')}</Text>
          </div>

          <Text className={commissionExplanationStyle}>{t('pricing:pricingInfo.commission.explanation.text')}</Text>
        </Accordion.Root>

        <div className={illustrationWrapperStyle}>
          <Illustration aria-hidden className={illustrationStyle} name="SlotMachine" strokeWidth={1.4} />
        </div>
      </section>
    </Card.Root>
  );
};
